import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faVial } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import { Label } from '@progress/kendo-react-labels';
import {
  NumericTextBox,
  type NumericTextBoxChangeEvent,
  type SwitchChangeEvent,
  TextArea,
  TextBox,
} from '@progress/kendo-react-inputs';
import { DatePicker, type DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import type { SamplingJobDialogProps } from './sampling-job-dialog.types.ts';
import type { SamplingJob, SamplingJobSampler, SamplingJobSampleType } from '../services/api.types.ts';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ListViewItemSeparator } from '../components/list/list-view-item-separator.tsx';
import { ListViewItem } from '../components/list/list-view-item.tsx';
import { ListView } from '../components/list/list-view.tsx';
import type { ListViewRenderProps } from '../components/list/list-view.types.ts';
import { ListViewItemText } from '../components/list/list-view-item-text.tsx';
import { ToggleSwitch } from '../components/input/toggle-switch.tsx';
import { getSamplingJobSamplers, getSamplingJobSampleTypes, updateSamplingJob } from '../services/api.ts';
import { getSamplingJobValidation } from '../utils/sampling-job.ts';
import { SamplingJobValidationStatus } from '../utils/sampling-job.type.ts';
import styles from './sampling-job-dialog.module.scss';

export function SamplingJobDialog({ job, jobStatuses, onApply, onClose }: SamplingJobDialogProps) {
  const [model, setModel] = useState<SamplingJob>({ ...job });
  const [jobSampleTypes, setJobSampleTypes] = useState<SamplingJobSampleType[]>([]);
  const [jobSamplers, setJobSamplers] = useState<SamplingJobSampler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [sampleTypes, samplers] = await Promise.all([
          getSamplingJobSampleTypes(job.id),
          getSamplingJobSamplers(job.id),
        ]);
        if (isMounted) {
          setJobSampleTypes(sampleTypes);
          setJobSamplers(samplers);
        }
      } catch (err) {
        console.error('Error loading dialog:', err);
        if (isMounted) {
          setError('Unable to load job properties');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [job.id]);

  const jobValidation = useMemo(() => {
    return getSamplingJobValidation(job);
  }, [job]);

  const renderSampleTypeItem = ({ dataItem }: ListViewRenderProps<SamplingJobSampleType>) => (
    <ListViewItem>
      <ListViewItemText flex={1}>
        <strong>
          {dataItem.sensorTypeTitle} - {dataItem.productGroupTitle}
        </strong>
      </ListViewItemText>
      <ListViewItemSeparator />
      <ListViewItemText flex={1}>
        <strong>No. Fields:&nbsp;</strong>
        {dataItem.fieldCount}
      </ListViewItemText>
      <ListViewItemSeparator />
      <ListViewItemText flex={1}>
        <strong>No. Samples:&nbsp;</strong>
        {dataItem.plannedCount}&nbsp;
        <em>({dataItem.takenCount})</em>
      </ListViewItemText>
    </ListViewItem>
  );

  const renderSamplerItem = ({ dataItem }: ListViewRenderProps<SamplingJobSampler>) => (
    <ListViewItem>
      <ListViewItemText flex={1}>
        <strong>{dataItem.username}</strong>
      </ListViewItemText>
      <ListViewItemSeparator />
      <ListViewItemText flex={1}>
        <strong>Forename:&nbsp;</strong>
        {dataItem.forename}
      </ListViewItemText>
      <ListViewItemSeparator />
      <ListViewItemText flex={1}>
        <strong>Surname:&nbsp;</strong>
        {dataItem.surname}
      </ListViewItemText>
    </ListViewItem>
  );

  const handleActiveChange = ({ value }: SwitchChangeEvent) => {
    setModel((prev) => ({ ...prev, active: value }));
  };

  const handleApply = async () => {
    if (model.estimatedDays == undefined) {
      setError('Please enter a valid estimated number of days.');
      return;
    }

    setLoading(true);
    try {
      await updateSamplingJob({
        jobId: model.id,
        plannedDate: model.plannedDate,
        estimatedDays: model.estimatedDays,
        statusId: model.statusId,
        active: model.active,
      });
      onApply();
    } catch (error) {
      console.error('Error updating job details:', error);
      setError('Failed to save job details');
      setLoading(false);
    }
  };

  const handleValueChange = ({ target }: NumericTextBoxChangeEvent) => {
    const { name, value } = target;
    setModel((prev) => ({ ...prev, [name!]: value }));
  };

  const handlePlannedChange = ({ value }: DatePickerChangeEvent) =>
    setModel((prev) => ({
      ...prev,
      plannedDate: value ? value.toISOString() : undefined,
    }));

  const handleView = () => {
    const redirectUri = encodeURIComponent(`/soilsamplingjob/index?jobId=${job.id}`);
    const uri = `${import.meta.env.VITE_KORE_URL}/common/changeclient?newclientid=${job.clientId}&redirect=${redirectUri}`;
    window.open(uri, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      title={
        <div className={styles.dialogTitle}>
          <FontAwesomeIcon icon={faVial} className={styles.titleIcon} />
          <span>Sampling Properties</span>
        </div>
      }
      className={styles.window}
      onClose={onClose}
    >
      <>
        {loading ? (
          <LoaderRender message={'Loading...'} />
        ) : (
          <>
            {error ? (
              <div className={`${styles.banner} ${styles.bannerError}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} size={'xl'} />
                <div className={styles.bannerSeparator}></div>
                <span>{error}</span>
              </div>
            ) : jobValidation.status === SamplingJobValidationStatus.error ? (
              <div className={`${styles.banner} ${styles.bannerError}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} size={'xl'} />
                <div className={styles.bannerSeparator}></div>
                <span>{jobValidation.description}</span>
              </div>
            ) : jobValidation.status === SamplingJobValidationStatus.warn ? (
              <div className={`${styles.banner} ${styles.bannerWarn}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} size={'xl'} />
                <div className={styles.bannerSeparator}></div>
                <span>{jobValidation.description}</span>
              </div>
            ) : null}
            <div className={styles.formGrid}>
              <Label>Company Title:</Label>
              <TextBox readOnly={true} value={model.companyTitle} />
              <Label>Client Title:</Label>
              <TextBox readOnly={true} value={model.clientTitle} />
              <Label>Client Ref:</Label>
              <TextBox readOnly={true} value={model.clientRef ?? '-'} />
              <Label>Job No:</Label>
              <TextBox readOnly={true} value={`SSJ${model.id}`} />
              <Label>Order No:</Label>
              <TextBox readOnly={true} value={model.orderNumber ?? '-'} />
              <Label>Planned Date:</Label>
              <DatePicker
                format={'dd MMM yyyy'}
                placeholder={'Set planned sampling date'}
                value={model.plannedDate ? new Date(model.plannedDate) : null}
                onChange={handlePlannedChange}
              />
              <Label>Estimated Days:</Label>
              <NumericTextBox
                name={'estimatedDays'}
                min={1}
                max={14}
                placeholder={'Set estimated no. of days to complete job'}
                value={model.estimatedDays}
                onChange={handleValueChange}
              />
              <Label>Job Status:</Label>
              <DropDownList
                dataItemKey={'id'}
                textField={'title'}
                value={jobStatuses.find((status) => status.id === model.statusId)}
                data={jobStatuses}
                onChange={({ value }) =>
                  setModel((prev) => ({
                    ...prev,
                    statusId: value.id,
                    statusTitle: value.title,
                  }))
                }
              />
              <Label>Job Active:</Label>
              <ToggleSwitch checked={model.active} size={'large'} onChange={handleActiveChange} />
              <div className={styles.descriptionWrapper}>
                <Label>Description:</Label>
                <TextArea resizable={'none'} readOnly={true} value={model.description ?? ''} rows={2} />
              </div>
            </div>
            <div className={styles.sectionHeader}>Sample Types:</div>
            <div className={styles.listContainer}>
              <ListView
                data={jobSampleTypes}
                item={renderSampleTypeItem}
                emptyMessage={'No sample types currently assigned for this job.'}
              />
            </div>
            <div className={styles.sectionHeader}>Assigned Samplers:</div>
            <div className={styles.listContainer}>
              <ListView
                data={jobSamplers}
                item={renderSamplerItem}
                emptyMessage={'No samplers currently assigned to this job.'}
              />
            </div>
          </>
        )}
      </>
      <DialogActionsBar>
        <div className={styles.actionsBar}>
          <Button themeColor={'secondary'} onClick={handleView}>
            View Sampling Job
          </Button>
          <div className={styles.actionButtonGroup}>
            <Button onClick={onClose}>Close</Button>
            <Button themeColor={'primary'} onClick={handleApply} disabled={loading}>
              Confirm
            </Button>
          </div>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
}
